import { useEffect, useRef, useState } from "react";

import AdminDashboard from "./components/HandcraftsAdmin";
import { CheckoutModal, ProductCard } from "./components/HandcraftsCommerce";
import { AboutPage, DeliveryPage, HomePage, NavBar, PaymentResultPage, ShopPage } from "./components/HandcraftsSiteViews";
import {
  API_URL,
  C,
  CSS,
  FEEDBACK,
  IG_HANDLE,
  IMG_LOGO,
  SEED_PRODUCTS_URL,
  T,
  calcCapacity,
  fmtMoney,
  normalizeProducts,
  todayStr,
} from "./data/handcraftsConfig";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const ADMIN_AUTH_URL = "./api/admin-auth.php";
const CUSTOM_ORDER_AI_URL = "./api/custom-order-ai.php";
const WOMPI_STATUS_URL = "./api/wompi-order-status.php";
const CATALOG_VERSION = "gallery-2026-08";

function getPageFromLocation() {
  const params = new URLSearchParams(window.location.search);
  return params.get("page") || "inicio";
}
// Fallback used when the AI endpoint is unavailable. Production time is one
// week across the whole line, so no entry carries its own duration.
const CUSTOM_ORDER_KEYWORDS = {
  virgen: { colors: "blanco, azul cielo y dorado", price: "$35-$70", ideas: ["rosario tejido", "halo dorado", "base decorativa"] },
  angel: { colors: "crema, dorado y beige", price: "$28-$55", ideas: ["alas con brillo", "detalle de corazón", "cinta personalizada"] },
  conejo: { colors: "crema, rosa pastel y blanco", price: "$24-$48", ideas: ["orejas largas", "lazo grande", "nombre bordado"] },
  conejita: { colors: "crema, rosa pastel y blanco", price: "$24-$48", ideas: ["orejas largas", "lazo grande", "nombre bordado"] },
  muneca: { colors: "rosa, lavanda y tonos piel", price: "$30-$65", ideas: ["cabello rizado", "vestido por capas", "accesorio personalizado"] },
  muñeca: { colors: "rosa, lavanda y tonos piel", price: "$30-$65", ideas: ["cabello rizado", "vestido por capas", "accesorio personalizado"] },
  bebe: { colors: "celeste, blanco y amarillo suave", price: "$20-$42", ideas: ["sonajero a juego", "mantita pequeña", "nombre bordado"] },
  bebé: { colors: "celeste, blanco y amarillo suave", price: "$20-$42", ideas: ["sonajero a juego", "mantita pequeña", "nombre bordado"] },
  navidad: { colors: "rojo, verde y dorado", price: "$18-$45", ideas: ["set de adornos", "detalle metálico", "versión mini para regalo"] }
};

function buildLocalDesignIdea(rawIdea) {
  const idea = rawIdea.trim();
  const normalized = idea.toLowerCase();
  const match = Object.entries(CUSTOM_ORDER_KEYWORDS).find(([keyword]) => normalized.includes(keyword));
  const profile = match?.[1] || {
    colors: "rosa viejo, crema y dorado suave",
    price: "$22-$55",
    ideas: ["tamaño mediano", "detalle personalizado", "versión para regalo"]
  };

  return [
    `1. Cómo lo tejería Abu`,
    `Abu puede tejer "${idea}" en estilo amigurumi, cuidando los detalles principales de la pieza.`,
    ``,
    `2. Colores sugeridos`,
    `${profile.colors}.`,
    ``,
    `3. Tiempo de trabajo`,
    `Una semana, igual que el resto de la línea.`,
    ``,
    `4. Precio estimado`,
    `${profile.price} USD, según tamaño, accesorios y personalización.`,
    ``,
    `5. Ideas de diseño`,
    `- ${profile.ideas[0]}`,
    `- ${profile.ideas[1]}`,
    `- ${profile.ideas[2]}`,
    ``,
    `Si querés, ajustá los colores, el nombre o el tamaño con Abu por Instagram antes de confirmar.`,
  ].join("\n");
}

function splitOrderByItems(order) {
  const safeItems = Array.isArray(order.items) ? order.items.filter(Boolean) : [];
  return safeItems.map((item, index) => ({
    ...order,
    id: `${order.id}-${index + 1}`,
    items: [item]
  }));
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer la imagen."));
    reader.readAsDataURL(file);
  });
}

export default function HandcraftsByAbu() {
  const [page, setPage]           = useState(() => getPageFromLocation());
  const [lang, setLang]           = useState("es");
  const t = T[lang]; // shorthand for current translations
  const [cart, setCart]           = useState([]);
  const [cartOpen, setCartOpen]   = useState(false);
  const [customOpen, setCustomOpen]= useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [idea, setIdea]           = useState("");
  const [customExamples, setCustomExamples] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [addedId, setAddedId]     = useState(null);
  const [orderSent, setOrderSent] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  const [draftCopyError, setDraftCopyError] = useState("");
  const [catFilter, setCatFilter] = useState("Todos");
  const [searchQ, setSearchQ]     = useState("");
  const [sortBy, setSortBy]       = useState("default");
  const [openFaq, setOpenFaq]     = useState(null);
  const [detailItem, setDetailItem]= useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try { const s = localStorage.getItem("abu_recently_viewed"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [menuOpen, setMenuOpen]   = useState(false);
  const [paymentResultStatus, setPaymentResultStatus] = useState("checking");

  // Sold-out tracking (single stock per product)
  const [soldIds, setSoldIds] = useState(() => {
    try { const s = localStorage.getItem("abu_sold"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const markSold = (productIds) => {
    setSoldIds(prev => {
      const next = [...new Set([...prev, ...productIds])];
      try { localStorage.setItem("abu_sold", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Admin
  const [adminMode, setAdminMode]   = useState(false);
  const [adminOrders, setAdminOrders] = useState(() => {
    try { const s = localStorage.getItem("abu_orders"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [products, setProducts] = useState([]);
  const [authOpen, setAuthOpen]   = useState(false);
  const [authError, setAuthError] = useState("");
  const [googleReady, setGoogleReady] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const logoClickCount            = useRef(0);
  const logoClickTimer            = useRef(null);
  const googleButtonRef           = useRef(null);
  const cacheProducts = nextProducts => {
    const normalized = normalizeProducts(nextProducts);
    setProducts(normalized);
    try { localStorage.setItem("abu_products_cache", JSON.stringify(normalized)); } catch {}
  };
  const updateProducts = updater => {
    setProducts(prev => {
      const next = normalizeProducts(typeof updater === "function" ? updater(prev) : updater);
      try { localStorage.setItem("abu_products_cache", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const loadSeedProducts = async () => {
    const response = await fetch(SEED_PRODUCTS_URL);
    if (!response.ok) throw new Error("No se pudo cargar el catálogo base.");
    return response.json();
  };

  const uploadSeedProducts = async (seedProducts) => {
    await Promise.all(seedProducts.map(product =>
      fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(product)
      }).catch(() => null)
    ));
  };

  useEffect(() => {
    let active = true;

    const loadProducts = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();

        const liveCatalog = Array.isArray(data.products)
          ? data.products.filter(product => product.catalogVersion === CATALOG_VERSION)
          : [];

        if (data.success && liveCatalog.length > 0) {
          if (active) cacheProducts(liveCatalog);
          return;
        }

        const seedProducts = await loadSeedProducts();
        if (active) cacheProducts(seedProducts);

        if (data.success) {
          uploadSeedProducts(seedProducts).catch(() => null);
        }
      } catch {
        try {
          const cached = localStorage.getItem("abu_products_cache");
          if (cached) {
            if (active) setProducts(normalizeProducts(JSON.parse(cached)));
            return;
          }
        } catch {}

        try {
          const seedProducts = await loadSeedProducts();
          if (active) cacheProducts(seedProducts);
        } catch {}
      }
    };

    loadProducts();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem("abu_orders", JSON.stringify(adminOrders)); } catch {}
  }, [adminOrders]);

  useEffect(() => {
    try { localStorage.setItem("abu_recently_viewed", JSON.stringify(recentlyViewed)); } catch {}
  }, [recentlyViewed]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("orderId");
    if (page !== "pago-resultado" || !orderId) {
      setPaymentResultStatus("checking");
      return undefined;
    }

    let cancelled = false;

    const loadPaymentResult = async () => {
      try {
        const res = await fetch(`${WOMPI_STATUS_URL}?orderId=${encodeURIComponent(orderId)}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setPaymentResultStatus(data?.order?.status || "checking");
        }
      } catch {
        if (!cancelled) setPaymentResultStatus("checking");
      }
    };

    loadPaymentResult();
    const intervalId = window.setInterval(loadPaymentResult, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [page]);

  useEffect(() => {
    const handlePopState = () => setPage(getPageFromLocation());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const pendingOrders = adminOrders.filter((order) => order.status === "payment_pending");
    if (!pendingOrders.length) return undefined;

    let cancelled = false;

    const syncPaymentStatus = async () => {
      try {
        const res = await fetch(WOMPI_STATUS_URL, { cache: "no-store" });
        if (!res.ok) return;

        const data = await res.json();
        const paidRefs = new Set(
          Object.entries(data?.orders || {})
            .filter(([, payment]) => payment?.status === "paid")
            .map(([orderId]) => String(orderId))
        );

        if (!paidRefs.size || cancelled) return;

        let changed = false;
        const paidProductIds = [];

        const nextOrders = adminOrders.map((order) => {
          if (order.status !== "payment_pending") return order;

          const paymentRef = String(order.paymentRef || order.id);
          if (!paidRefs.has(paymentRef)) return order;

          changed = true;
          if (Array.isArray(order.items)) {
            paidProductIds.push(...order.items.map((item) => item.productId));
          }

          return {
            ...order,
            status: "pending",
            isPaid: true,
            payMethod: order.payMethod || "Wompi"
          };
        });

        if (!changed || cancelled) return;

        persistOrders(nextOrders);
        if (paidProductIds.length) {
          markSold(paidProductIds);
        }
      } catch {
        // Keep pending orders visible until the next status refresh succeeds.
      }
    };

    syncPaymentStatus();
    const intervalId = window.setInterval(syncPaymentStatus, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [adminOrders]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        setAuthOpen(true);
        setAuthError("");
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch(ADMIN_AUTH_URL, { credentials: "same-origin", cache: "no-store" })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data?.user) return;
        setAdminUser(data.user);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    const existing = document.querySelector('script[data-google-identity="true"]');
    if (existing) {
      if (window.google?.accounts?.id) setGoogleReady(true);
      return undefined;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = "true";
    script.onload = () => setGoogleReady(true);
    document.head.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, []);

  useEffect(() => {
    if (!authOpen || !googleReady || !GOOGLE_CLIENT_ID || !googleButtonRef.current || !window.google?.accounts?.id) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async ({ credential }) => {
        try {
          const res = await fetch(ADMIN_AUTH_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ credential })
          });
          const data = await res.json();

          if (!res.ok || !data.success) {
            setAuthError(data.error || "No se pudo abrir el panel. Intentá otra vez.");
            return;
          }

          setAdminUser(data.user);
          setAdminMode(true);
          setAuthOpen(false);
          setAuthError("");
        } catch {
          setAuthError("No se pudo contactar al servidor. Revisá tu conexión e intentá otra vez.");
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true
    });

    googleButtonRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: "outline",
      size: "large",
      width: 240,
      text: "signin_with",
      shape: "pill"
    });
  }, [authOpen, googleReady]);

  useEffect(() => {
    const pageMeta = {
      inicio: {
        title: "Handcrafts By Abu | Amigurumis tejidos en El Salvador",
        description: "Amigurumis y piezas religiosas tejidas en El Salvador. Una semana de trabajo por pieza, con envío a todo el país."
      },
      tienda: {
        title: "Tienda | Handcrafts By Abu",
        description: "Piezas tejidas por Abu, con disponibilidad clara y pago con Wompi."
      },
      entrega: {
        title: "Entrega | Handcrafts By Abu",
        description: "Tiempos y zonas de entrega para El Salvador y Estados Unidos."
      },
      nosotros: {
        title: "Nosotros | Handcrafts By Abu",
        description: "La historia de Abu y el proceso detrás de cada pieza."
      },
      "pago-resultado": {
        title: "Resultado de Pago | Handcrafts By Abu",
        description: "Revisá el estado de tu pago y de tu pedido."
      }
    };

    const meta = pageMeta[page] || pageMeta.inicio;
    document.title = meta.title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) descriptionTag.setAttribute("content", meta.description);
  }, [page]);

  const persistOrders = orders => setAdminOrders(orders);
  const openProductDetail = product => {
    setDetailItem(product);
    setRecentlyViewed(prev => [product, ...prev.filter(item => item.id !== product.id)].slice(0, 4));
  };

  const handleSaveProduct = async (product) => {
    const catalogProduct = { ...product, catalogVersion: product.catalogVersion || CATALOG_VERSION };
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(catalogProduct)
      });
      const data = await res.json();
      if (data.success) {
        // Use the img path returned by server (may differ from base64)
        const saved = { ...catalogProduct, img: data.img || catalogProduct.img };
        updateProducts(prev => {
          const exists = prev.find(p => p.id === saved.id);
          return exists ? prev.map(p => p.id === saved.id ? saved : p) : [...prev, saved];
        });
      }
    } catch {
      // fallback: save locally if API is down
      updateProducts(prev => {
        const exists = prev.find(p => p.id === catalogProduct.id);
        return exists ? prev.map(p => p.id === catalogProduct.id ? catalogProduct : p) : [...prev, catalogProduct];
      });
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await fetch(API_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
    } catch {}
    updateProducts(prev => prev.filter(p => p.id !== id));
  };

  const allProducts = products;
  const suggestedProducts = recentlyViewed
    .filter(item => allProducts.some(product => product.id === item.id))
    .filter(item => !detailItem || item.id !== detailItem.id);

  const handleLogoClick = () => {
    logoClickCount.current += 1;
    if (logoClickTimer.current) clearTimeout(logoClickTimer.current);
    if (logoClickCount.current >= 3) {
      logoClickCount.current = 0;
      setAuthOpen(true);
      setAuthError("");
    } else {
      logoClickTimer.current = setTimeout(() => { logoClickCount.current = 0; }, 1200);
    }
  };

  const handleAdminExit = () => {
    setAdminMode(false);
    setAdminUser(null);
    fetch(ADMIN_AUTH_URL, { method: "DELETE", credentials: "same-origin" }).catch(() => {});
  };
  const { active: activeOrders, max: maxOrders } = calcCapacity(adminOrders);
  const capacityFull = activeOrders >= maxOrders;
  const effectiveSoldIds = capacityFull ? allProducts.map((product) => product.id) : soldIds;

  if (adminMode) return <AdminDashboard orders={adminOrders} products={products} setOrders={persistOrders} soldIds={effectiveSoldIds} onSaveProduct={handleSaveProduct} onDeleteProduct={handleDeleteProduct} onRestock={id=>{ setSoldIds(p=>{ const n=p.filter(x=>x!==id); try{localStorage.setItem("abu_sold",JSON.stringify(n));}catch{} return n; }); }} onExit={handleAdminExit}/>;

  // Cart helpers
  const addToCart = p => {
    if (capacityFull || effectiveSoldIds.includes(p.id)) return;
    setCart(prev => {
      const ex = prev.find(x => x.id===p.id);
      return ex ? prev.map(x=>x.id===p.id?{...x,qty:x.qty+1}:x) : [...prev,{...p,qty:1}];
    });
    setAddedId(p.id);
    setTimeout(()=>setAddedId(null), 1400);
  };
  const removeItem = id => setCart(prev=>prev.filter(x=>x.id!==id));
  const decrementItem = id => setCart(prev => prev.flatMap(item => item.id !== id ? [item] : item.qty > 1 ? [{ ...item, qty: item.qty - 1 }] : []));
  const incrementItem = id => {
    if (capacityFull) return;
    setCart(prev => prev.map(item => item.id === id ? { ...item, qty: item.qty + 1 } : item));
  };
  const total      = cart.reduce((s,p)=>s+p.price*p.qty,0);
  const count      = cart.reduce((s,p)=>s+p.qty,0);
  const closeCustom= ()=>{ setCustomOpen(false); setResult(null); setIdea(""); setCustomExamples([]); setOrderSent(false); setDraftMessage(""); setDraftCopyError(""); };
  const goto       = p  =>{
    const nextUrl = p === "inicio" ? window.location.pathname : `${window.location.pathname}?page=${encodeURIComponent(p)}`;
    window.history.pushState({}, "", nextUrl);
    setPage(p);
    setMenuOpen(false);
    window.scrollTo({top:0,behavior:"smooth"});
  };

  const handleCustomExampleFiles = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 3);
    event.target.value = "";
    if (!files.length) return;

    try {
      const nextExamples = await Promise.all(
        files.map(async (file) => ({
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          dataUrl: await readFileAsDataUrl(file)
        }))
      );
      setCustomExamples((prev) => [...prev, ...nextExamples].slice(0, 3));
      setDraftCopyError("");
    } catch {
      setDraftCopyError(t.custom_examples_error);
    }
  };

  const removeCustomExample = (id) => {
    setCustomExamples((prev) => prev.filter((example) => example.id !== id));
  };

  const createInstagramDraft = () => {
    const draftLines = [
      "Hola Abu,",
      ``,
      `Quiero hacer este encargo especial:`,
      idea.trim(),
      ``,
      `Idea sugerida:`,
      result || "Quiero hablar con vos sobre el diseño.",
      ...(customExamples.length
        ? ["", `Adjunto ${customExamples.length} imagen(es) de referencia para el estilo.`]
        : []),
      ``,
      `Mi usuario / contacto:`,
      `____________________`,
      ``,
      `Gracias.`,
    ];
    return draftLines.join("\n");
  };

  const openInstagramDraft = async () => {
    const draft = createInstagramDraft();
    setDraftMessage(draft);
    setDraftCopyError("");

    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      setDraftCopyError(t.custom_copy_error);
    }

    window.open(`https://www.instagram.com/${IG_HANDLE}/`, "_blank", "noopener,noreferrer");
    setOrderSent(true);
  };

  const buscarPatron = async () => {
    if (!idea.trim()) return;
    setLoading(true); setResult(null);
    try {
      const response = await fetch(CUSTOM_ORDER_AI_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            idea,
            examples: customExamples.map((example) => ({
              name: example.name,
              dataUrl: example.dataUrl
            }))
          })
      });
      const data = await response.json();

      if (response.ok && data.success && data.text) {
        setResult(data.text);
      } else {
        setResult(buildLocalDesignIdea(idea));
      }
    } catch {
      setResult(buildLocalDesignIdea(idea));
    }
    setLoading(false);
  };


  return (
    <div style={{minHeight:"100vh",background:C.background,color:C.ink}}>
      <style>{CSS}</style>
      <NavBar
        count={count}
        goto={goto}
        handleLogoClick={handleLogoClick}
        lang={lang}
        menuOpen={menuOpen}
        onCartOpen={()=>setCartOpen(true)}
        onCustomOpen={()=>setCustomOpen(true)}
        onToggleLanguage={()=>setLang(l=>l==="es"?"en":"es")}
        page={page}
        setMenuOpen={setMenuOpen}
        t={t}
      />

      {page==="inicio" && (
        <HomePage
          ProductCard={ProductCard}
          addedId={addedId}
          addToCart={addToCart}
          allProducts={allProducts}
          goto={goto}
          recentProducts={suggestedProducts}
          setCheckoutOpen={()=>setCheckoutOpen(true)}
          setCustomOpen={()=>setCustomOpen(true)}
          setDetailItem={openProductDetail}
            soldIds={effectiveSoldIds}
          t={t}
        />
      )}
        {page==="tienda" && (
          <ShopPage
          ProductCard={ProductCard}
          addedId={addedId}
          addToCart={addToCart}
          allProducts={allProducts}
          catFilter={catFilter}
          lang={lang}
          recentProducts={suggestedProducts}
          searchQ={searchQ}
          setCatFilter={setCatFilter}
          setCheckoutOpen={()=>setCheckoutOpen(true)}
          setCustomOpen={()=>setCustomOpen(true)}
          setDetailItem={openProductDetail}
          setSearchQ={setSearchQ}
          setSortBy={setSortBy}
            soldIds={effectiveSoldIds}
          sortBy={sortBy}
          t={t}
          />
        )}
        {page==="entrega" && (
          <DeliveryPage
            t={t}
          />
        )}
      {page==="pago-resultado" && (
        <PaymentResultPage
          orderId={new URLSearchParams(window.location.search).get("orderId") || ""}
          paymentStatus={paymentResultStatus}
          t={t}
        />
      )}
      {page==="nosotros" && (
          <AboutPage
          goto={goto}
          openFaq={openFaq}
          setCustomOpen={()=>setCustomOpen(true)}
          setOpenFaq={setOpenFaq}
          t={t}
        />
      )}

      {!cartOpen && !checkoutOpen && !customOpen && !detailItem && (
        <div style={{position:"fixed",left:12,right:12,bottom:12,zIndex:45,display:"flex",gap:10}} className="mobile-actions">
          <button
            className="btn btn-solid"
            style={{flex:1,padding:"14px 0"}}
            onClick={()=>setCartOpen(true)}
          >
            {t.nav_carrito} {count>0 ? `(${count})` : ""}
          </button>
          <button
            className="btn btn-outline"
            style={{flex:1,padding:"14px 0",background:C.surface}}
            onClick={()=>setCustomOpen(true)}
          >
            {lang==="es" ? "Encargo a medida" : "Custom order"}
          </button>
        </div>
      )}

      {/* Cart modal */}
      {cartOpen && (
        <div className="overlay" onClick={()=>setCartOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:460}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={{fontSize:"var(--text-body)"}}>{t.cart_title}</h2>
              <button className="btn btn-ghost" onClick={()=>setCartOpen(false)} style={{fontSize:"var(--text-body)",padding:"4px 10px"}}>✕</button>
            </div>
            {cart.length===0?(
              <div style={{textAlign:"center",padding:"32px 0",color:C.ink}}>
                <div style={{fontSize:"var(--text-body)",marginBottom:12}}>{t.cart_empty_icon}</div>
                <p className="sans">{t.cart_empty}</p>
                <button className="btn btn-solid" style={{marginTop:16}} onClick={()=>{setCartOpen(false);goto("tienda");}}>{t.cart_ver_tienda}</button>
              </div>
            ) : (
              <>
                {cart.map(p=>(
                  <div key={p.id} className="cart-item">
                    <img src={p.img} alt={p.name} style={{width:60,height:60,objectFit:"cover",borderRadius:2,border:`1.5px solid ${C.rule}`}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"var(--text-body)",color:C.ink}}>{p.name}</div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,flexWrap:"wrap"}}>
                        <button onClick={()=>decrementItem(p.id)} style={{background:C.surface,border:`1px solid ${C.rule}`,cursor:"pointer",fontSize:"var(--text-body)",color:C.ink,padding:"4px 10px",borderRadius:2}}>-</button>
                        <div style={{fontSize:"var(--text-body)",color:C.ink,minWidth:28,textAlign:"center"}}>{p.qty}</div>
                        <button onClick={()=>incrementItem(p.id)} disabled={capacityFull} style={{background:C.surface,border:`1px solid ${C.rule}`,cursor:capacityFull?"not-allowed":"pointer",fontSize:"var(--text-body)",color:C.ink,padding:"4px 10px",borderRadius:2,opacity:capacityFull?0.5:1}}>+</button>
                        <div style={{fontSize:"var(--text-body)",color:C.ink}}>{fmtMoney(p.price*p.qty)}</div>
                      </div>
                    </div>
                    <button onClick={()=>removeItem(p.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"var(--text-label)",color:FEEDBACK.error,padding:"4px 8px",whiteSpace:"nowrap"}}>{t.cart_remove_all}</button>
                  </div>
                ))}
                <div style={{borderTop:`2px solid ${C.rule}`,marginTop:16,paddingTop:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
                    <span className="sans" style={{fontSize:"var(--text-body)"}}>{t.cart_subtotal}</span>
                    <span className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{fmtMoney(total)}</span>
                  </div>
                  <p className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginBottom:14}}>{t.cart_envio}</p>
                  <button className="btn btn-solid btn-full" style={{fontSize:"var(--text-body)",padding:"14px 0",opacity:capacityFull?0.5:1,cursor:capacityFull?"not-allowed":"pointer"}} disabled={capacityFull} onClick={()=>{if(capacityFull) return; setCartOpen(false);setCheckoutOpen(true);}}>
                    {t.cart_pagar}
                  </button>
                  {capacityFull && <p className="sans" style={{fontSize:"var(--text-label)",color:FEEDBACK.error,marginTop:10}}>Abu tiene 20 pedidos activos, el máximo. Escribile por Instagram para que te avise cuando se libere un espacio.</p>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Product detail modal */}
      {detailItem && (
        <div className="overlay" onClick={()=>setDetailItem(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:500,maxHeight:"92vh",overflowY:"auto"}}>
            <button className="btn btn-ghost" onClick={()=>setDetailItem(null)} style={{float:"right",fontSize:"var(--text-body)",padding:"4px 10px"}}>✕</button>
            <div style={{background:C.background,borderRadius:2,marginBottom:20,display:"flex",alignItems:"center",justifyContent:"center",minHeight:280,maxHeight:420,overflow:"hidden"}}><img src={detailItem.img} alt={detailItem.name} style={{width:"100%",maxHeight:420,objectFit:"contain",borderRadius:2,display:"block"}}/></div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <span style={{fontSize:"var(--text-body)",color:C.ink}}>${detailItem.price}</span>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button className="btn btn-outline" style={{flex:1}} onClick={()=>{addToCart(detailItem);setDetailItem(null);}}>{t.detail_al_carrito}</button>
              <button className="btn btn-solid"   style={{flex:1}} onClick={()=>{addToCart(detailItem);setDetailItem(null);setCheckoutOpen(true);}}>{t.detail_comprar}</button>
            </div>
            {suggestedProducts.length > 0 && (
              <div style={{marginTop:24,borderTop:`1.5px solid ${C.rule}`,paddingTop:20}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:12}}>
                  {suggestedProducts.slice(0,3).map((product) => (
                    <button
                      key={product.id}
                      onClick={()=>openProductDetail(product)}
                      style={{background:C.surface,border:`1.5px solid ${C.rule}`,borderRadius:2,padding:10,cursor:"pointer",textAlign:"left"}}
                    >
                      <div style={{background:C.background,borderRadius:2,height:100,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}>
                        <img src={product.img} alt={product.name} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} />
                      </div>
                      <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,lineHeight:1.35}}>{product.name}</div>
                      <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginTop:4}}>${product.price}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Checkout modal */}
        {checkoutOpen && cart.length>0 && (
          <CheckoutModal
            cart={cart}
            onClose={()=>setCheckoutOpen(false)}
            onSuccess={(orderId, method, customerInfo) => {
              const newOrder = {
                id: orderId,
                createdAt: todayStr(),
                status: "payment_pending",
                customer: {
                  name: customerInfo.name || "Cliente web",
                  phone: customerInfo.phone || "N/A",
                  instagram: customerInfo.instagram || customerInfo.email || "N/A",
                  email: customerInfo.email || ""
                },
                address: {
                  street: customerInfo.address || "",
                  city: customerInfo.city || "",
                  dept: customerInfo.city || "",
                  country: customerInfo.country || "El Salvador",
                  notes: customerInfo.notes || ""
                },
                shippingZone: customerInfo.shippingZone || "sv_metro",
                items: cart.map(p => ({ productId:p.id, name:p.name, qty:p.qty, price:p.price })),
                notes: `Pedido web. Pago: ${method}.${customerInfo.notes ? ` Notas: ${customerInfo.notes}` : ""}`,
                isPaid: false,
                payMethod: method,
                paymentRef: orderId
              };
              const splitOrders = splitOrderByItems(newOrder);
              const nextOrders = [...adminOrders, ...splitOrders];
              persistOrders(nextOrders);
              setCart([]);
            }}
          />
        )}

      {/* Custom order modal */}
      {customOpen && (
        <div className="overlay" onClick={closeCustom}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <h2 style={{fontSize:"var(--text-body)"}}>{t.custom_title}</h2>
              <button className="btn btn-ghost" onClick={closeCustom} style={{fontSize:"var(--text-body)",padding:"4px 10px"}}>✕</button>
            </div>
            <p className="sans" style={{color:C.ink,fontSize:"var(--text-body)",marginBottom:22,lineHeight:1.7}}>{t.custom_sub}</p>
            {!orderSent?(
              <>
                <label className="sans" style={{fontSize:"var(--text-body)",display:"block",marginBottom:8,color:C.ink}}>{t.custom_label}</label>
                <textarea value={idea} onChange={e=>setIdea(e.target.value)} placeholder={t.custom_placeholder} rows={4}/>
                <div style={{marginTop:14}}>
                  <label className="sans" style={{fontSize:"var(--text-body)",display:"block",marginBottom:8,color:C.ink}}>{t.custom_examples_label}</label>
                  <input type="file" accept="image/*" multiple onChange={handleCustomExampleFiles} />
                  <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginTop:8,lineHeight:1.5}}>{t.custom_examples_hint}</div>
                  {customExamples.length > 0 && (
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(96px,1fr))",gap:12,marginTop:14}}>
                      {customExamples.map((example) => (
                        <div key={example.id} style={{background:C.surface,borderRadius:2,padding:10}}>
                          <img
                            src={example.dataUrl}
                            alt={example.name}
                            style={{width:"100%",aspectRatio:"1 / 1",objectFit:"cover",borderRadius:2,display:"block"}}
                          />
                          <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginTop:8,wordBreak:"break-word"}}>{example.name}</div>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => removeCustomExample(example.id)}
                            style={{fontSize:"var(--text-label)",padding:"8px 10px",marginTop:6,width:"100%"}}
                          >
                            {t.custom_examples_remove}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button className="btn btn-solid btn-full" style={{marginTop:14,fontSize:"var(--text-body)",padding:"13px 0"}} onClick={buscarPatron} disabled={loading||!idea.trim()}>
                  {loading?t.custom_btn_loading:t.custom_btn}
                </button>
                {loading&&<div className="spinner"/>}
                {result&&(
                  <>
                    <div className="pattern-box">{result}</div>
                    <div style={{marginTop:18,padding:"16px 20px",background:C.surface,borderRadius:2,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
                      <div>
                        <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{t.custom_gusto}</div>
                        <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginTop:2}}>{t.custom_ig}</div>
                      </div>
                      <button className="btn btn-solid" onClick={openInstagramDraft} style={{fontSize:"var(--text-body)"}}>{t.custom_hacer}</button>
                    </div>
                  </>
                )}
              </>
            ):(
              <div style={{textAlign:"center",padding:"32px 0"}}>
                <h3 style={{fontSize:"var(--text-body)",marginBottom:10}}>{t.custom_sent_title}</h3>
                <p className="sans" style={{color:C.ink,fontSize:"var(--text-body)",lineHeight:1.75,maxWidth:360,margin:"0 auto 26px"}}>
                  {t.custom_sent_sub} <strong>@{IG_HANDLE}</strong>
                </p>
                {draftCopyError && (
                  <p className="sans" style={{color:FEEDBACK.error,fontSize:"var(--text-label)",margin:"0 auto 12px",maxWidth:360}}>
                    {draftCopyError}
                  </p>
                )}
                {draftMessage && (
                  <div style={{textAlign:"left",background:C.surface,borderRadius:2,padding:"16px 18px",margin:"0 auto 18px",maxWidth:420}}>
                    <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:8}}>{t.custom_draft_label}</div>
                    <textarea readOnly value={draftMessage} rows={10} style={{width:"100%",resize:"vertical"}} />
                  </div>
                )}
                <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                  <button className="btn btn-outline" onClick={openInstagramDraft} style={{fontSize:"var(--text-body)",padding:"12px 22px"}}>{t.custom_copy_again}</button>
                  <a className="btn btn-solid" href={`https://www.instagram.com/${IG_HANDLE}/`} target="_blank" rel="noreferrer" style={{fontSize:"var(--text-body)",padding:"12px 22px",textDecoration:"none"}}>{t.custom_open_ig}</a>
                  <button className="btn btn-solid" onClick={closeCustom} style={{fontSize:"var(--text-body)",padding:"12px 22px"}}>{t.custom_sent_btn}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {authOpen && (
        <div className="overlay" onClick={()=>setAuthOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:360,textAlign:"center",padding:"36px 28px"}}>
            <img src={IMG_LOGO} alt="Handcrafts By Abu" style={{width:56,height:56,objectFit:"contain",marginBottom:"var(--space-block)"}}/>
            <div className="label" style={{marginBottom:12}}>Panel</div>
            <h3 style={{fontSize:"var(--text-body)",marginBottom:6,color:C.ink}}>{lang==="es" ? "Acceso de administradora" : "Admin access"}</h3>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:18,lineHeight:1.7}}>
              {lang==="es"
                ? "Entrá con una de las cuentas de Google autorizadas."
                : "Sign in with one of the approved Google accounts."}
            </p>
            {!GOOGLE_CLIENT_ID && (
              <p className="sans" style={{color:FEEDBACK.error,fontSize:"var(--text-label)",marginBottom:12}}>
                {lang==="es"
                  ? "Falta REACT_APP_GOOGLE_CLIENT_ID. Agregalo antes de compilar."
                  : "REACT_APP_GOOGLE_CLIENT_ID is missing. Add it before building."}
              </p>
            )}
            {authError && (
              <p className="sans" style={{color:FEEDBACK.error,fontSize:"var(--text-label)",marginBottom:12}}>
                {authError}
              </p>
            )}
            {adminUser ? (
              <button className="btn btn-solid btn-full" onClick={()=>{setAdminMode(true);setAuthOpen(false);}}>
                Continuar como {adminUser.name}
              </button>
            ) : (
              <div ref={googleButtonRef} style={{display:"flex",justifyContent:"center",minHeight:44,marginBottom:14}} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
